import { isInstalledApp } from "./pwa";

/**
 * "Install the app" support for the merchant dashboard.
 *
 * Chrome (Android and desktop) hands the page an event when the site can be
 * installed, and no longer shows a banner of its own. We keep that event so the
 * page can offer the install itself. iPhone has no such event: there the
 * merchant adds the app from the Share menu, so we can only show the steps.
 */

type Listener = () => void;

let promptEvent: any = null;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((listener) => listener());

/** Call once, as early as possible: the browser fires the event only once per page load. */
export function watchForInstallPrompt(): void {
	window.addEventListener("beforeinstallprompt", (event) => {
		event.preventDefault();
		promptEvent = event;
		notify();
	});
	window.addEventListener("appinstalled", () => {
		promptEvent = null;
		alreadyInstalled = true;
		notify();
	});
	checkAlreadyInstalled();
}

/** Re-render when the browser offers (or withdraws) the install. Returns an unsubscribe function. */
export function onInstallChange(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** True when a tap can open the browser's own install dialog. */
export const canPromptInstall = (): boolean => promptEvent !== null;

export const isIos = (): boolean =>
	/iphone|ipad|ipod/i.test(navigator.userAgent) ||
	// iPadOS reports itself as a Mac; a Mac has no touch screen.
	(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isAndroid = (): boolean => /android/i.test(navigator.userAgent);

/** Chrome, Edge and other Chromium browsers: the only ones that can install a site from the page. */
const browserCanInstall = (): boolean => "BeforeInstallPromptEvent" in window || "onbeforeinstallprompt" in window;

// Set once the browser tells us the app is already on this device (Android Chrome only).
let alreadyInstalled = false;

/**
 * Whether to offer the install at all. Never inside the installed app, not when
 * the browser reports it is already installed, and not in a desktop browser that
 * cannot install sites (Safari, Firefox) where the offer would lead nowhere.
 *
 * This does NOT wait for the browser's own "can be installed" signal: Chrome only
 * gives that after the visitor has spent a while on the site, so a first-time
 * visitor would never see the offer. Without the signal the button shows the steps.
 */
export const shouldOfferInstall = (): boolean =>
	!isInstalledApp() && !alreadyInstalled && (isIos() || isAndroid() || browserCanInstall());

/** Asks the browser whether the app is already installed, where it can tell. */
export function checkAlreadyInstalled(): void {
	const lookup = (navigator as any).getInstalledRelatedApps;
	if (typeof lookup !== "function") return;
	lookup
		.call(navigator)
		.then((apps: any[]) => {
			if (Array.isArray(apps) && apps.length > 0) {
				alreadyInstalled = true;
				notify();
			}
		})
		.catch(() => undefined);
}

export type InstallResult = "accepted" | "dismissed" | "unavailable";

/**
 * Opens the browser's install dialog. "unavailable" means the browser could not
 * open it (nothing on offer, or it refused), so the caller should show the steps.
 */
export async function promptInstall(): Promise<InstallResult> {
	if (!promptEvent) return "unavailable";
	const event = promptEvent;
	// The event can be used only once.
	promptEvent = null;
	try {
		await event.prompt();
		const choice = await event.userChoice;
		notify();
		return choice?.outcome === "accepted" ? "accepted" : "dismissed";
	} catch {
		notify();
		return "unavailable";
	}
}

/** The steps to show when the browser cannot open an install dialog for us. */
export function manualInstallSteps(): string {
	if (isIos()) {
		return "Open this page in Safari, tap the Share button, then choose “Add to Home Screen”.";
	}
	if (isAndroid()) {
		return "Open the browser menu (⋮) at the top right, then choose “Add to Home screen” or “Install app”.";
	}
	return "In Chrome or Edge, click the install icon at the right end of the address bar, or open the browser menu and choose “Install Styola Orders”.";
}

const REQUESTED_KEY = "styola-install-requested";

/**
 * The banner is not shown to every visitor. It appears once the visitor has shown
 * they want to sell (they tapped "Start Selling"), and stays for that browser tab.
 */
export function requestInstallBanner(): void {
	try {
		sessionStorage.setItem(REQUESTED_KEY, "1");
	} catch {
		// Private browsing: the sidebar option is still there after login.
	}
	notify();
}

export function installBannerRequested(): boolean {
	try {
		return sessionStorage.getItem(REQUESTED_KEY) === "1";
	} catch {
		return false;
	}
}

const DISMISSED_KEY = "styola-install-dismissed-at";
const ASK_AGAIN_AFTER_DAYS = 7;

/** The banner stays away for a week after "Not now"; the sidebar option is always there. */
export function installBannerDismissed(): boolean {
	try {
		const at = Number(localStorage.getItem(DISMISSED_KEY) || 0);
		return at > 0 && Date.now() - at < ASK_AGAIN_AFTER_DAYS * 24 * 60 * 60 * 1000;
	} catch {
		return false;
	}
}

export function dismissInstallBanner(): void {
	try {
		localStorage.setItem(DISMISSED_KEY, String(Date.now()));
	} catch {
		// Private browsing: the banner simply shows again next visit.
	}
}
